import React from 'react';
import { ChevronRight, Package, Building2, Monitor, Settings, Search, Info, X, CreditCard } from 'lucide-react';
import { useProductMatrix } from '../contexts/ProductMatrixContext';
import { useTabGeneration } from '../hooks/useTabGeneration';
import { useProductProcessing } from '../hooks/useProductProcessing';
import { hasComingSoonForCountry, sortByAvailability } from '../utils/tableHelpers';
import { HardwareTableSection } from './tables/HardwareTableSection';
import { SoftwareTableSection } from './tables/SoftwareTableSection';
import { Level2PlatformTableSection } from './tables/Level2PlatformTableSection';
import { PartnerIntegrationsTable } from './tables/PartnerIntegrationsTable';
import { SearchResultsTable } from './tables/SearchResultsTable';
import type { OnboardingSystem } from '../pages/ProductMatrixNew';

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface PlatformDetailsViewProps {
  COUNTRIES: Country[];
  getCountryFlag: (countryName: string) => string;
}

const PlatformDetailsView: React.FC<PlatformDetailsViewProps> = ({ COUNTRIES, getCountryFlag }) => {
  // Get all state from ProductMatrixContext
  const {
    // Navigation State
    selectedPlatform,
    currentView,
    browseMode,
    selectedCountry,

    // Product Data
    platformProducts,
    lbm2Products,
    onboardingSystems,
    onboardingColumnGroups,

    // UI State
    platformLoading,
    activeTab,
    setActiveTab,
    tabSearchQuery,
    setTabSearchQuery,
    highlightedProduct,
    isDisclaimerDismissed,

    // Modal Handlers
    handleDeviceClick,
    handleProductClick,
    handlePartnerClick,
    handleOnboardingClick,
  } = useProductMatrix();

  // Use product processing hook
  const {
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    searchVisibleCountries,
    useCompactMode,
    level2Platforms,
    level2SearchResults,
    platformDescription,
  } = useProductProcessing({
    platformProducts,
    selectedPlatform,
    tabSearchQuery,
    products: platformProducts, // Pass all products
    COUNTRIES,
    selectedCountry,
    currentView,
    browseMode,
  });

  // Generate tabs using the hook
  const tabs = useTabGeneration({
    selectedPlatform,
    platformProducts,
    products: platformProducts,
    hardwareProducts,
    softwareProducts,
    unifiedVisibleCountries,
    level2Platforms,
    lbm2Products,
    onboardingSystems,
    COUNTRIES,
    browseMode,
    selectedCountry,
  });

  if (!selectedPlatform) {
    return null;
  }

  return (
    <div className="pb-[20vh]">
      {/* Platform Content */}
      {platformLoading ? (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex items-center justify-between">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-64"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-24"></div>
          </div>

          {/* Navigation Skeleton */}
          <div className="flex gap-2">
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-32"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-28"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-36"></div>
          </div>

          {/* Table Skeleton */}
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse w-full"></div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse w-full"></div>
            ))}
          </div>
        </div>
      ) : (
        <div className="relative">
          {/* Navigation & Tabs Container */}
          
          {/* Tabbed Content */}
          <div className="space-y-2 px-0 sm:px-6 lg:px-0">

          {/* Platform Header */}
          <div className="mb-2">
            <h1 className="text-2xl font-semibold text-foreground mb-2">
              {selectedPlatform}
            </h1>
            {platformDescription && (
              <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                {platformDescription}
              </p>
            )}
          </div>

          {/* Sticky Header with Tabs + Table Header */}
          {tabs.length > 0 && (
            <div className={`sticky z-20 bg-background border-b border-border pb-2 -mt-2 8 ${
              isDisclaimerDismissed ? 'top-[0px]' : 'top-[0px]'
            }`}>
              {/* Tab Navigation */}
              <nav className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 bg-background pt-4">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                  {tabs.map((tab) => {
                    const isActive = activeTab === tab.id && !tabSearchQuery.trim();

                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setTabSearchQuery(''); // Clear search when clicking a tab
                        }}
                        className={`flex items-center px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap transition-all ${
                          isActive
                            ? 'bg-black dark:bg-blue-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Search Input - Rightmost on desktop, full width on mobile */}
                <div className="relative w-full md:w-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search tabs..."
                    value={tabSearchQuery}
                    onChange={(e) => setTabSearchQuery(e.target.value)}
                    className={`w-full md:min-w-[200px] pl-10 pr-3 py-2 text-sm border rounded-md focus:outline-none bg-transparent dark:bg-gray-800 dark:text-white ${
                      tabSearchQuery.trim()
                        ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800 focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                  {tabSearchQuery && (
                    <button
                      onClick={() => setTabSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </nav>

              {/* Dynamic Table Header based on Active Tab - Only show on large screens (1024px+) */}
              <div className="hidden lg:block">
                {(() => {
                // When searching in non-Integrated-Payments platforms, show a simplified header with just country columns
                if (tabSearchQuery.trim() && selectedPlatform !== 'Integrated Payments') {
                  const columnWidth = searchVisibleCountries.length >= 14 ? '45px' : '60px';
                  const allLevel2SearchProducts = Object.values(level2SearchResults).flatMap(p => p.products);
                  const allSearchProducts = [...hardwareProducts, ...softwareProducts, ...allLevel2SearchProducts];

                  return (
                    <table className="w-full" style={{tableLayout: 'fixed'}}>
                      <thead className="bg-transparent">
                        <tr className="h-16">
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '300px'}}></th>
                          {browseMode ? (
                            searchVisibleCountries.map(country => (
                              <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent dark:bg-gray-900 cursor-default select-none" style={{width: columnWidth}} title={country.name}>
                                <div className="flex flex-col items-center justify-center h-full">
                                  {searchVisibleCountries.length >= 14 ? (
                                    <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{country.code}</span>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center h-full gap-1">
                                      <span className="text-sm select-none">{country.flag}</span>
                                      <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                      {hasComingSoonForCountry(allSearchProducts, country.code) && (
                                        <span className="text-[10px] italic text-muted-foreground select-none">coming soon</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </th>
                            ))
                          ) : (
                            selectedCountry && (
                              <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent dark:bg-gray-900 cursor-default select-none" style={{width: columnWidth}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                <div className="flex flex-col items-center justify-center h-full gap-1">
                                  <span className="text-sm select-none">{COUNTRIES.find(c => c.code === selectedCountry)?.flag}</span>
                                  <span className="text-xs select-none whitespace-nowrap">{selectedCountry}</span>
                                </div>
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                    </table>
                  );
                }

                // Show compact mode logic - but we'll still show headers for 14+ columns now
                // (removed the early returns that hid headers for 14+ columns)

                // Show global header for non-compact mode
                if (activeTab === 'supported-hardware' && hardwareProducts.length > 0) {
                  return (
                    <table className="w-full" style={{tableLayout: 'fixed'}}>
                      <thead className="bg-transparent">
                        <tr className="h-16">
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '300px'}}></th>
                          {browseMode ? (
                            unifiedVisibleCountries.map(country => (
                              <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: '60px'}} title={country.name}>
                                <div className="flex flex-col items-center justify-center h-full gap-1">
                                  <span className="text-sm select-none">{country.flag}</span>
                                  <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                  {hasComingSoonForCountry(hardwareProducts, country.code) && (
                                    <span className="text-[10px] italic text-muted-foreground select-none">coming soon</span>
                                  )}
                                </div>
                              </th>
                            ))
                          ) : (
                            <>
                              {selectedCountry && (
                                <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: '60px'}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                  <div className="flex flex-col items-center justify-center h-full gap-1">
                                    <span className="text-sm select-none">{COUNTRIES.find(c => c.code === selectedCountry)?.flag}</span>
                                    <span className="text-xs select-none whitespace-nowrap">{selectedCountry}</span>
                                  </div>
                                </th>
                              )}
                            </>
                          )}
                        </tr>
                      </thead>
                    </table>
                  );
                } else if (activeTab === 'product-modules' && softwareProducts.length > 0) {
                  return (
                    <table className="w-full" style={{tableLayout: 'fixed'}}>
                      <thead className="bg-transparent">
                        <tr className="h-16">
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '200px'}}></th>
                          {browseMode ? (
                            unifiedVisibleCountries.map(country => (
                              <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: '60px'}} title={country.name}>
                                <div className="flex flex-col items-center justify-center h-full gap-1">
                                  <span className="text-sm select-none">{country.flag}</span>
                                  <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                  {hasComingSoonForCountry(softwareProducts, country.code) && (
                                    <span className="text-[10px] italic text-muted-foreground select-none">coming soon</span>
                                  )}
                                </div>
                              </th>
                            ))
                          ) : (
                            selectedCountry && (
                              <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent" style={{width: '60px'}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                <div className="flex flex-col items-center justify-center h-full">
                                  <span className="text-xs whitespace-nowrap">{selectedCountry}</span>
                                </div>
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                    </table>
                  );
                } else if (activeTab === 'supported-payments') {
                  // Supported Payments tab header
                  const tabVisibleCountries = unifiedVisibleCountries;
                  const tabColumnWidth = tabVisibleCountries.length >= 14 ? '45px' : '60px';
                  const supportedPaymentsForHeader = level2Platforms['Shift4 Payments']?.products.filter(
                    product => product.category !== 'Local Payment Methods'
                  ) || [];
                  const isSkyTabPlatform = selectedPlatform === 'SkyTab' || selectedPlatform === 'SkyTab Venue';

                  return (
                    <table className="w-full" style={{tableLayout: 'fixed'}}>
                      <thead className="bg-transparent">
                        <tr className="h-16">
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '200px'}}></th>
                          {browseMode ? (
                            tabVisibleCountries.map(country => {
                              // Special case: For SkyTab/SkyTab Venue, always show "coming soon" for Spain and Australia
                              const isSpainOrAustralia = country.code === 'Spain' || country.code === 'Australia';
                              const showComingSoon = isSkyTabPlatform && isSpainOrAustralia;

                              return (
                                <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: tabColumnWidth}} title={country.name}>
                                  <div className="flex flex-col items-center justify-center h-full">
                                    {tabVisibleCountries.length >= 14 ? (
                                      <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{country.code}</span>
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-full gap-1">
                                        <span className="text-sm select-none">{country.flag}</span>
                                        <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                        {(showComingSoon || hasComingSoonForCountry(supportedPaymentsForHeader, country.code)) && (
                                          <span className="text-[10px] italic text-muted-foreground select-none">coming soon</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </th>
                              );
                            })
                          ) : (
                            selectedCountry && (
                              <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: tabColumnWidth}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                <div className="flex flex-col items-center justify-center h-full">
                                  {tabVisibleCountries.length >= 14 ? (
                                    <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{selectedCountry}</span>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center h-full gap-1">
                                      <span className="text-sm select-none">{COUNTRIES.find(c => c.code === selectedCountry)?.flag}</span>
                                      <span className="text-xs select-none whitespace-nowrap">{selectedCountry}</span>
                                    </div>
                                  )}
                                </div>
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                    </table>
                  );
                } else if (activeTab.startsWith('level2-')) {
                  // Special case: 'level2-payments' maps to 'Shift4 Payments'
                  let platformKey = activeTab === 'level2-payments'
                    ? 'Shift4 Payments'
                    : Object.keys(level2Platforms).find(key =>
                        `level2-${key.toLowerCase().replace(/\s+/g, '-')}` === activeTab
                      );
                  if (platformKey && level2Platforms[platformKey]) {
                    const showTagColumn = false; // Tag column removed from all integrations tables

                    // For Payment Methods tab, use all countries
                    const tabVisibleCountries = activeTab === 'level2-payments' && level2Platforms['Shift4 Payments']
                      ? COUNTRIES
                      : unifiedVisibleCountries;

                    const tabColumnWidth = tabVisibleCountries.length >= 14 ? '45px' : '60px';
                    const level2Products = level2Platforms[platformKey].products;

                    return (
                      <table className="w-full" style={{tableLayout: 'fixed'}}>
                        <thead className="bg-transparent">
                          <tr className="h-16">
                            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '200px'}}></th>
                            {showTagColumn && (
                              <th className="text-left px-3 py-2 text-xs font-medium text-foreground bg-transparent" style={{width: '120px'}}>Tag</th>
                            )}
                            {browseMode ? (
                              tabVisibleCountries.map(country => (
                                <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: tabColumnWidth}} title={country.name}>
                                  <div className="flex flex-col items-center justify-center h-full">
                                    {tabVisibleCountries.length >= 14 ? (
                                      <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{country.code}</span>
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-full gap-1">
                                        <span className="text-sm select-none">{country.flag}</span>
                                        <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                        {hasComingSoonForCountry(level2Products, country.code) && (
                                          <span className="text-[10px] italic text-muted-foreground select-none">coming soon</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </th>
                              ))
                            ) : (
                              selectedCountry && (
                                <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: tabColumnWidth}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                  <div className="flex flex-col items-center justify-center h-full">
                                    {tabVisibleCountries.length >= 14 ? (
                                      <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{selectedCountry}</span>
                                    ) : (
                                      <div className="flex flex-col items-center justify-center h-full gap-1">
                                        <span className="text-sm select-none">{COUNTRIES.find(c => c.code === selectedCountry)?.flag}</span>
                                        <span className="text-xs select-none whitespace-nowrap">{selectedCountry}</span>
                                      </div>
                                    )}
                                  </div>
                                </th>
                              )
                            )}
                          </tr>
                        </thead>
                      </table>
                    );
                  }
                } else if (activeTab === 'level3-lbm-2.0' && lbm2Products.length > 0) {
                  // LBM 2.0 tab header
                  const tabColumnWidth = unifiedVisibleCountries.length >= 14 ? '45px' : '60px';

                  return (
                    <table className="w-full" style={{tableLayout: 'fixed'}}>
                      <thead className="bg-transparent">
                        <tr className="h-16">
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '200px'}}></th>
                          {browseMode ? (
                            unifiedVisibleCountries.map(country => (
                              <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: tabColumnWidth}} title={country.name}>
                                <div className="flex flex-col items-center justify-center h-full">
                                  {unifiedVisibleCountries.length >= 14 ? (
                                    <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{country.code}</span>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center h-full gap-1">
                                      <span className="text-sm select-none">{country.flag}</span>
                                      <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                      {hasComingSoonForCountry(lbm2Products, country.code) && (
                                        <span className="text-[10px] italic text-muted-foreground select-none">coming soon</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </th>
                            ))
                          ) : (
                            selectedCountry && (
                              <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent cursor-default select-none" style={{width: tabColumnWidth}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                <div className="flex flex-col items-center justify-center h-full gap-1">
                                  <span className="text-sm select-none">{COUNTRIES.find(c => c.code === selectedCountry)?.flag}</span>
                                  <span className="text-xs select-none whitespace-nowrap">{selectedCountry}</span>
                                </div>
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                    </table>
                  );
                } else if (activeTab.startsWith('vertical-') && selectedPlatform === 'Integrated Payments') {
                  // Integrated Payments vertical tabs header - desktop sticky headers
                  const columnWidth = unifiedVisibleCountries.length >= 14 ? '45px' : '60px';
                  return (
                    <table className="w-full" style={{tableLayout: 'fixed'}}>
                      <thead className="bg-transparent">
                        <tr className="h-16">
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '180px'}}>Partner</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '180px'}}>Solution</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '120px'}}>Hardware Family</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '100px'}}>Integration Type</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '80px'}}>Version</th>
                          <th className="text-center px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '100px'}}>
                            <div className="flex flex-col items-center gap-0.5">
                              <span>Shift4 Mobile</span>
                              <span className="text-xs text-muted-foreground font-normal">(Pay / Order at the Table)</span>
                            </div>
                          </th>
                          {browseMode ? (
                            unifiedVisibleCountries.map(country => (
                              <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent relative group cursor-default select-none" style={{width: columnWidth}} title={country.name}>
                                <div className="flex flex-col items-center justify-center h-full">
                                  {unifiedVisibleCountries.length >= 14 ? (
                                    <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{country.code}</span>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center h-full gap-1">
                                      <span className="text-sm select-none">{country.flag}</span>
                                      <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                    </div>
                                  )}
                                </div>
                              </th>
                            ))
                          ) : (
                            selectedCountry && (
                              <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent relative group cursor-default select-none" style={{width: columnWidth}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                <div className="flex flex-col items-center justify-center h-full">
                                  {unifiedVisibleCountries.length >= 14 ? (
                                    <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{selectedCountry}</span>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center h-full gap-1">
                                      <span className="text-sm select-none">{COUNTRIES.find(c => c.code === selectedCountry)?.flag}</span>
                                      <span className="text-xs select-none whitespace-nowrap">{selectedCountry}</span>
                                    </div>
                                  )}
                                </div>
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                    </table>
                  );
                }

                // Return null if no matching tab
                return null;
                })()}
              </div>
            </div>
          )}

          {/* Tab Content */}
          {tabs.length > 0 && (
            <div className="space-y-6">

              {/* Active Tab Content */}
              {(() => {
                // When searching, show all matching results regardless of tab (but only for non-Integrated Payments platforms)
                if (tabSearchQuery.trim() && selectedPlatform !== 'Integrated Payments') {
                  // Use level2SearchResults calculated earlier (after tabs generation)
                  const hasLevel2Results = Object.keys(level2SearchResults).length > 0;
                  const allSearchResults = [...hardwareProducts, ...softwareProducts];

                  if (allSearchResults.length > 0 || hasLevel2Results) {
                    return (
                      <div className="space-y-6">
                        {hardwareProducts.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                              <Monitor className="w-5 h-5" />
                              {tabs.find(tab => tab.id === 'supported-hardware')?.label || 'Hardware'} Results ({hardwareProducts.length})
                            </h3>
                            <SearchResultsTable
                              products={hardwareProducts}
                              visibleCountries={searchVisibleCountries}
                              type="hardware"
                              browseMode={browseMode}
                              selectedCountry={selectedCountry}
                              highlightedProduct={highlightedProduct}
                              onProductClick={(product, type) => type === 'hardware' ? handleDeviceClick(product) : handleProductClick(product)}
                            />
                          </div>
                        )}
                        {softwareProducts.length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                              <Settings className="w-5 h-5" />
                              {tabs.find(tab => tab.id === 'product-modules')?.label || 'Software'} Results ({softwareProducts.length})
                            </h3>
                            <SearchResultsTable
                              products={softwareProducts}
                              visibleCountries={searchVisibleCountries}
                              type="software"
                              browseMode={browseMode}
                              selectedCountry={selectedCountry}
                              highlightedProduct={highlightedProduct}
                              onProductClick={(product, type) => type === 'hardware' ? handleDeviceClick(product) : handleProductClick(product)}
                            />
                          </div>
                        )}
                        {hasLevel2Results && Object.entries(level2SearchResults).map(([platformName, platformData]) => {
                          const displayName = platformName === 'SkyTab Marketplace Integrations' ? 'SkyTab Marketplace' : platformName;
                          return (
                            <div key={platformName}>
                              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                <Settings className="w-5 h-5" />
                                {displayName} Results ({platformData.products.length})
                              </h3>
                              <SearchResultsTable
                                products={platformData.products}
                                visibleCountries={searchVisibleCountries}
                                type="level2"
                                browseMode={browseMode}
                                selectedCountry={selectedCountry}
                                highlightedProduct={highlightedProduct}
                                onProductClick={(product, type) => type === 'hardware' ? handleDeviceClick(product) : handleProductClick(product)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Search className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          No results found for "{tabSearchQuery}"
                        </p>
                        <p className="text-muted-foreground text-center text-sm mt-2">
                          Try searching with different keywords
                        </p>
                      </div>
                    );
                  }
                } else if (activeTab === 'supported-partners') {
                  // Supported Partners tab for Shift4 Mobile
                  const supportedPartners = platformProducts.filter(p => p.patt && p.patt.trim() !== '');

                  if (supportedPartners.length > 0) {
                    // Render table with same structure as Integrated Payments
                    const columnWidth = unifiedVisibleCountries.length >= 14 ? '45px' : '60px';
                    const filteredPartners = supportedPartners.filter(product => {
                      if (browseMode) {
                        return unifiedVisibleCountries.some(country => product.countries[country.code] === true);
                      } else {
                        return selectedCountry && product.countries[selectedCountry] === true;
                      }
                    });

                    const sortedPartners = sortByAvailability(filteredPartners);

                    return (
                      <div className="bg-card border border-border rounded-lg overflow-x-auto">
                        <table className="w-full min-w-[600px]" style={{tableLayout: 'fixed'}}>
                          <thead className="bg-transparent">
                            <tr className="h-16">
                              <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '180px'}}>Partner</th>
                              <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '180px'}}>Solution</th>
                              <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '120px'}}>Hardware Family</th>
                              <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '100px'}}>Integration Type</th>
                              <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '80px'}}>Version</th>
                              <th className="text-center px-4 py-2 text-sm font-medium text-foreground bg-transparent" style={{width: '100px'}}>
                                <div className="flex flex-col items-center gap-0.5">
                                  <span>Shift4 Mobile</span>
                                  <span className="text-xs text-muted-foreground font-normal">(Pay / Order at the Table)</span>
                                </div>
                              </th>
                              {browseMode ? (
                                unifiedVisibleCountries.map(country => (
                                  <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-transparent relative group cursor-default select-none" style={{width: columnWidth}} title={country.name}>
                                    <div className="flex flex-col items-center justify-center h-full">
                                      {unifiedVisibleCountries.length >= 14 ? (
                                        <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{country.code}</span>
                                      ) : (
                                        <div className="flex flex-col items-center justify-center h-full gap-1">
                                          <span className="text-sm select-none">{country.flag}</span>
                                          <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                                        </div>
                                      )}
                                    </div>
                                  </th>
                                ))
                              ) : (
                                selectedCountry && (
                                  <th className="text-center px-1 py-2 font-medium text-foreground bg-transparent relative group cursor-default select-none" style={{width: columnWidth}} title={COUNTRIES.find(c => c.code === selectedCountry)?.name}>
                                    <div className="flex flex-col items-center justify-center h-full">
                                      {unifiedVisibleCountries.length >= 14 ? (
                                        <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{selectedCountry}</span>
                                      ) : (
                                        <div className="flex flex-col items-center justify-center h-full gap-1">
                                          <span className="text-sm select-none">{COUNTRIES.find(c => c.code === selectedCountry)?.flag}</span>
                                          <span className="text-xs select-none whitespace-nowrap">{selectedCountry}</span>
                                        </div>
                                      )}
                                    </div>
                                  </th>
                                )
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedPartners.map((product, index) => (
                              <tr
                                key={index}
                                className="border-b border-border/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-sm cursor-pointer group"
                                onClick={() => handlePartnerClick(product)}
                                title="Click to view integration details"
                              >
                                <td className="px-4 py-3 text-sm font-medium truncate" style={{width: '180px'}}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div className="font-medium text-foreground text-sm leading-tight truncate">{product.solution}</div>
                                      {product.notes && (
                                        <div title="Additional details available">
                                          <Info className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-blue-600 text-xs font-medium ml-2 flex-shrink-0">
                                      <span>View</span>
                                      <ChevronRight className="w-3 h-3" />
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground truncate" style={{width: '180px'}}>
                                  {product.solution_description || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground truncate" style={{width: '120px'}}>
                                  {product.hardware_family || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground truncate" style={{width: '100px'}}>
                                  {product.model || '-'}
                                </td>
                                <td className="px-4 py-3 text-xs text-muted-foreground truncate" style={{width: '80px'}}>
                                  {product.tag || '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-center" style={{width: '100px'}}>
                                  {product.patt && product.patt.trim() !== '' ? (
                                    <span className="text-muted-foreground">{product.patt}</span>
                                  ) : (
                                    <span className="text-transparent">-</span>
                                  )}
                                </td>
                                {browseMode ? (
                                  unifiedVisibleCountries.map(country => {
                                    const value = product.countries?.[country.code];
                                    return (
                                      <td key={country.code} className="text-center px-1 py-2" style={{width: columnWidth}}>
                                        {value === true ? (
                                          <span className="text-sm text-green-600">✓</span>
                                        ) : value === 'Coming Soon' ? (
                                          <span className="text-transparent">-</span>
                                        ) : (
                                          <span className="text-sm text-red-500">✗</span>
                                        )}
                                      </td>
                                    );
                                  })
                                ) : (
                                  selectedCountry && (() => {
                                    const value = product.countries?.[selectedCountry];
                                    return (
                                      <td className="text-center px-1 py-2" style={{width: columnWidth}}>
                                        {value === true ? (
                                          <span className="text-sm text-green-600">✓</span>
                                        ) : value === 'Coming Soon' ? (
                                          <span className="text-transparent">-</span>
                                        ) : (
                                          <span className="text-sm text-red-500">✗</span>
                                        )}
                                      </td>
                                    );
                                  })()
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Package className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          No supported partners with Pay at the Table
                        </p>
                      </div>
                    );
                  }
                } else if (activeTab === 'supported-hardware' && tabs.find(tab => tab.id === 'supported-hardware')) {
                  if (hardwareProducts.length > 0) {
                    return (
                      <HardwareTableSection
                        products={hardwareProducts}
                        visibleCountries={unifiedVisibleCountries}
                        includeHeader={useCompactMode}
                        browseMode={browseMode}
                        selectedCountry={selectedCountry}
                        onDeviceClick={handleDeviceClick}
                        countries={COUNTRIES}
                      />
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Monitor className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          Currently there is nothing to show here
                        </p>
                      </div>
                    );
                  }
                } else if (activeTab === 'level2-payments') {
                  // Defensive check: only render if Payments platform data exists
                  if (level2Platforms['Shift4 Payments']) {
                    // For Payment Methods tab, use all countries instead of filtering by availability
                    const paymentsVisibleCountries = COUNTRIES;
                    return (
                      <Level2PlatformTableSection
                        platformName="Shift4 Payments"
                        platformData={level2Platforms['Shift4 Payments']}
                        visibleCountries={paymentsVisibleCountries}
                        includeHeader={false}
                        browseMode={browseMode}
                        selectedCountry={selectedCountry}
                        selectedPlatform={selectedPlatform}
                        onProductClick={handleProductClick}
                        countries={COUNTRIES}
                        highlightedProduct={highlightedProduct}
                      />
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Package className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          Currently there is nothing to show here
                        </p>
                      </div>
                    );
                  }
                } else if (activeTab === 'supported-payments') {
                  // Supported Payments tab for SkyTab, SkyTab Venue, and Shift4 Mobile
                  if (level2Platforms['Shift4 Payments']) {
                    const supportedPayments = level2Platforms['Shift4 Payments'].products.filter(
                      product => product.category !== 'Local Payment Methods'
                    );

                    // For Shift4 Mobile: Filter countries to only show those available in Supported Partners
                    let paymentsVisibleCountries = unifiedVisibleCountries;
                    if (selectedPlatform === 'Shift4 Mobile') {
                      // Get countries available in Supported Partners (PATT products)
                      const supportedPartners = platformProducts.filter(p => p.patt && p.patt.trim() !== '');
                      paymentsVisibleCountries = unifiedVisibleCountries.filter(country =>
                        supportedPartners.some(partner => partner.countries[country.code] === true)
                      );
                    }

                    return (
                      <Level2PlatformTableSection
                        platformName="Supported Payments"
                        platformData={{
                          platform_description: 'Payment methods supported by this platform',
                          products: supportedPayments
                        }}
                        visibleCountries={paymentsVisibleCountries}
                        includeHeader={false}
                        browseMode={browseMode}
                        selectedCountry={selectedCountry}
                        selectedPlatform={selectedPlatform}
                        onProductClick={handleProductClick}
                        countries={COUNTRIES}
                        highlightedProduct={highlightedProduct}
                      />
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <CreditCard className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          No payment methods available
                        </p>
                      </div>
                    );
                  }
                } else if (activeTab === 'product-modules' && tabs.find(tab => tab.id === 'product-modules')) {
                  if (softwareProducts.length > 0) {
                    return (
                      <SoftwareTableSection
                        products={softwareProducts}
                        visibleCountries={unifiedVisibleCountries}
                        includeHeader={useCompactMode}
                        browseMode={browseMode}
                        selectedCountry={selectedCountry}
                        onProductClick={handleProductClick}
                        countries={COUNTRIES}
                        highlightedProduct={highlightedProduct}
                      />
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Settings className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          Currently there is nothing to show here
                        </p>
                      </div>
                    );
                  }
                } else if (activeTab === 'level3-lbm-2.0') {
                  // LBM 2.0 Level 3 platform tab
                  if (lbm2Products.length > 0) {
                    return (
                      <Level2PlatformTableSection
                        platformName="LBM 2.0"
                        platformData={{
                          platform_description: 'Advanced version of Lighthouse Business Manager with enhanced tools for restaurants.',
                          products: lbm2Products
                        }}
                        visibleCountries={unifiedVisibleCountries}
                        includeHeader={false}
                        browseMode={browseMode}
                        selectedCountry={selectedCountry}
                        selectedPlatform={selectedPlatform}
                        onProductClick={handleProductClick}
                        countries={COUNTRIES}
                        highlightedProduct={highlightedProduct}
                      />
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          No LBM 2.0 products available
                        </p>
                      </div>
                    );
                  }
                } else if (activeTab.startsWith('vertical-') && selectedPlatform === 'Integrated Payments') {
                  // Integrated Payments - Handle search across all verticals OR specific vertical tab
                  if (tabSearchQuery.trim()) {
                    // When searching, get ALL ISV Partner Integration products that match the search
                    const searchResults = platformProducts.filter(product => {
                      // Only include ISV Partner Integration products
                      if (product.category !== 'ISV Partner Integration') {
                        return false;
                      }

                      const query = tabSearchQuery.toLowerCase();
                      const searchableText = [
                        product.solution,
                        product.solution_description,
                        product.tag,
                        product.vertical,
                        product.notes
                      ].filter(Boolean).join(' ').toLowerCase();

                      return searchableText.includes(query);
                    });

                    // Filter for Ready status only for Integrated Payments platform
                    const readySearchResults = searchResults.filter(product => product.development === 'Ready');

                    if (readySearchResults.length > 0) {
                      // Group search results by vertical for organized display
                      const groupedResults = readySearchResults.reduce((groups, product) => {
                        let vertical = product.vertical;
                        // Normalize vertical names for display
                        if (vertical === 'F&B') vertical = 'Food & Beverage';
                        if (vertical === 'Auto Rental') vertical = 'Auto';
                        if (vertical.startsWith('Ecommerce')) vertical = 'Ecommerce';
                        if (vertical === 'Moto') vertical = 'MOTO';
                        if (vertical.includes('Vending')) vertical = vertical.split('(')[0].trim();

                        if (!groups[vertical]) groups[vertical] = [];
                        groups[vertical].push(product);
                        return groups;
                      }, {} as Record<string, typeof searchResults>);

                      return (
                        <div className="space-y-6">
                          {Object.entries(groupedResults).map(([verticalName, products]) => (
                            <div key={verticalName}>
                              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                <Package className="w-5 h-5" />
                                {verticalName} Results ({products.length})
                              </h3>
                              <PartnerIntegrationsTable
                                products={products}
                                visibleCountries={unifiedVisibleCountries}
                                browseMode={browseMode}
                                selectedCountry={selectedCountry}
                                highlightedProduct={highlightedProduct}
                                onPartnerClick={handlePartnerClick}
                              />
                            </div>
                          ))}
                        </div>
                      );
                    } else {
                      return (
                        <div className="flex flex-col items-center justify-center py-12">
                          <Search className="w-12 h-12 text-muted-foreground mb-4" />
                          <p className="text-muted-foreground text-center">
                            No results found for "{tabSearchQuery}"
                          </p>
                          <p className="text-muted-foreground text-center text-sm mt-2">
                            Try searching with different keywords
                          </p>
                        </div>
                      );
                    }
                  } else {
                    // Normal vertical tab display (no search)
                    const verticalTabId = activeTab.replace('vertical-', '');

                    // Get products for this specific vertical
                    let verticalProducts;

                    // Special case for "All" tab - show all ISV Partner Integration products
                    if (verticalTabId === 'all') {
                      verticalProducts = platformProducts.filter(product => product.category === 'ISV Partner Integration');
                    } else if (verticalTabId === 'patt') {
                      // Special case for "Pay at the Table" tab - show only products with patt has a value
                      verticalProducts = platformProducts.filter(product => product.category === 'ISV Partner Integration' && product.patt && product.patt.trim() !== '');
                    } else {
                      verticalProducts = platformProducts.filter(product => {
                        // Only include ISV Partner Integration products
                        if (product.category !== 'ISV Partner Integration') {
                          return false;
                        }

                        const productVertical = product.vertical;

                        // Special case for auto tab - include both "Auto" and "Auto Rental"
                        if (verticalTabId === 'auto') {
                          return productVertical === 'Auto' || productVertical === 'Auto Rental';
                        }

                        // Normalize matching for comparison
                        let normalizedVertical = productVertical;
                        if (productVertical === 'F&B') normalizedVertical = 'Food & Beverage';
                        if (productVertical === 'Auto Rental') normalizedVertical = 'Auto';
                        if (productVertical.startsWith('Ecommerce')) normalizedVertical = 'Ecommerce';
                        if (productVertical === 'Moto') normalizedVertical = 'MOTO';
                        if (productVertical.includes('Vending')) normalizedVertical = productVertical.split('(')[0].trim();

                        // Convert normalized vertical to tab format for comparison
                        const verticalAsTabId = normalizedVertical.toLowerCase().replace(/\s+/g, '-').replace('&', 'and');
                        return verticalAsTabId === verticalTabId;
                      });
                    }

                    // Filter for Ready status only for Integrated Payments platform
                    verticalProducts = verticalProducts.filter(product => product.development === 'Ready');

                    if (verticalProducts.length > 0) {
                    return <PartnerIntegrationsTable
                      products={verticalProducts}
                      visibleCountries={unifiedVisibleCountries}
                      browseMode={browseMode}
                      selectedCountry={selectedCountry}
                      highlightedProduct={highlightedProduct}
                      onPartnerClick={handlePartnerClick}
                    />;
                    } else {
                      return (
                        <div className="flex flex-col items-center justify-center py-12">
                          <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                          <p className="text-muted-foreground text-center">
                            No products available for this vertical
                          </p>
                        </div>
                      );
                    }
                  }
                } else if (activeTab === 'onboarding') {
                  // Onboarding tab - show country cards with onboarding systems
                  if (onboardingSystems.length > 0 && onboardingColumnGroups) {
                    // Group systems by country
                    const systemsByCountry = onboardingSystems.reduce((acc, system) => {
                      if (!acc[system.country]) {
                        acc[system.country] = [];
                      }
                      acc[system.country].push(system);
                      return acc;
                    }, {} as Record<string, OnboardingSystem[]>);

                    // Get countries that have product modules available for this platform
                    // Only check Level 1 products with category "Solution" (core platform solutions, not hardware)
                    const countriesWithProducts = new Set<string>();
                    platformProducts
                      .filter(product => String(product.solution_level) === "1" && product.category === "Solution")
                      .forEach(product => {
                        Object.entries(product.countries).forEach(([countryCode, isAvailable]) => {
                          if (isAvailable === true) {
                            countriesWithProducts.add(countryCode);
                            // Also add country name (for matching with onboarding data)
                            const countryInfo = COUNTRIES.find(c => c.code === countryCode);
                            if (countryInfo) {
                              countriesWithProducts.add(countryInfo.name);
                            }
                          }
                        });
                      });

                    // Filter to only show countries that have products available
                    const filteredSystemsByCountry = Object.entries(systemsByCountry).filter(([country]) => {
                      return countriesWithProducts.has(country);
                    });

                    // Card grid layout - simplified clickable country cards
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {filteredSystemsByCountry.map(([country, systems]) => {
                          const countryInfo = COUNTRIES.find(c => c.code === country || c.name === country);
                          const countryFlag = countryInfo?.flag || getCountryFlag(country);
                          const countryName = countryInfo?.name || country;

                          return (
                            <div
                              key={country}
                              onClick={() => handleOnboardingClick({
                                country,
                                countryName,
                                countryFlag,
                                systems
                              })}
                              className="bg-transparent border border-border rounded-lg p-6 shadow-sm cursor-pointer hover:shadow-md hover:border-blue-400 transition-all group"
                            >
                              <div className="flex flex-col items-center justify-center text-center gap-3">
                                <span className="text-5xl">{countryFlag}</span>
                                <h3 className="text-lg font-semibold text-foreground group-hover:text-blue-600 transition-colors">
                                  {countryName}
                                </h3>
                                <p className="text-xs text-muted-foreground">Click for details</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          No onboarding data available
                        </p>
                      </div>
                    );
                  }
                } else {
                  // Other Level 2 platform tabs (Integrations)
                  const platformKey = Object.keys(level2Platforms).find(key =>
                    `level2-${key.toLowerCase().replace(/\s+/g, '-')}` === activeTab
                  );
                  if (platformKey && level2Platforms[platformKey]) {
                    return (
                      <Level2PlatformTableSection
                        platformName={platformKey}
                        platformData={level2Platforms[platformKey]}
                        visibleCountries={unifiedVisibleCountries}
                        includeHeader={false}
                        browseMode={browseMode}
                        selectedCountry={selectedCountry}
                        selectedPlatform={selectedPlatform}
                        onProductClick={handleProductClick}
                        countries={COUNTRIES}
                        highlightedProduct={highlightedProduct}
                      />
                    );
                  } else {
                    return (
                      <div className="flex flex-col items-center justify-center py-12">
                        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-center">
                          Currently there is nothing to show here
                        </p>
                      </div>
                    );
                  }
                }
              })()}
            </div>
          )}
          </div>
        </div>
      )}

    </div>
  );
};

export default PlatformDetailsView;
